import { removeDupes, cleanObject, isTempId } from '../utils/helpers';

const incrementCount = initialCount => (initialCount || 0) + 1;
const decrementCount = initialCount => Math.max((initialCount || 0) - 1, 0);

const getGroupKey = payload => {
    if (payload.from || payload.to || payload.completedFrom || payload.completedTo || payload.completedBy) {
        return false;
    }

    if (payload.type) {
        return payload.type;
    }

    if (payload.archived) {
        return 'archived';
    }

    if (payload.taskId) {
        return 'task';
    }

    if (payload.taskGroupId) {
        return 'taskGroup';
    }

    if (payload.meeting_id) {
        return 'meetingId';
    }

    if (payload.userId) {
        if (payload.projectId) {
            return 'user-project';
        } else if (payload.completed || payload.completedBy) {
            return 'user-completed';
        } else {
            return 'user-open';
        }
    }

    if (payload.completed) {
        return 'completed';
    }

    if (payload.projectId) {
        return 'project';
    }

    if (payload.milestoneId) {
        return 'milestone';
    }

    return 'team';
}

const getIDFromPayload = payload => payload.taskId || payload.taskGroupId || payload.projectId || payload.meeting_id || payload.userId || payload.milestoneId;

const DEFAULT_STATE = {
    items: [],
    fetchInfo: {
        taskGroup: {},
        task: {},
        project: {},
        'user-project': {},
        'user-open': {},
        'user-completed': {},
        milestone: {},
        team: {},
        archived: {},
        completed: {},
        personal: {},
        meetingId: {}
    },
    allTasksLoading: false,
    allTasksFetched: false,
    filter: {
        term: '',
        user: null,
        dueDate: null,
        milestone: null,
        label: null
    }
};

const tasks = (state = {...DEFAULT_STATE}, action) => {
    if (action.error) {
        return state;
    }

    switch (action.type) {
        case 'TEAM_CHANGE':
        case 'RESET_STORE':
            return {...DEFAULT_STATE}

        case 'MARK_ALL_FOR_REFETCH':
            return Object.assign({}, state, {
                items: [...state.items.map(task => ({
                    ...task,
                    shouldRefetch: true
                }))]
            });

        case 'TASKS_FETCH_REQUESTED': {
            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);

            if (!key) {
                return state;
            }

            const updatedFetchInfo = id ? {
                ...state.fetchInfo[key],
                [id]: {
                    ...state.fetchInfo[key][id],
                    loading: true
                }
            } : {
                ...state.fetchInfo[key],
                loading: true
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: updatedFetchInfo
                }
            });
        }

        case 'TASKS_FETCH_SUCCEEDED': {
            if (!action.payload.tasks) {
                return state;
            }

            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);
            const items = removeDupes([...action.payload.tasks, ...state.items]);

            const updatedState = { items };

            if (key) {
                const updatedFetchInfo = id ? {
                    ...state.fetchInfo[key],
                    [id]: {
                        fetched: true,
                        hasMore: action.payload.hasMore,
                        loading: false
                    }
                } : {
                    fetched: true,
                    hasMore: action.payload.hasMore,
                    loading: false
                }

                updatedState.fetchInfo = {
                    ...state.fetchInfo,
                    [key]: updatedFetchInfo
                }
            }

            return Object.assign({}, state, updatedState);
        }

        case 'TASKS_FETCH_FAILED': {
            const key = getGroupKey(action.payload);
            const id = getIDFromPayload(action.payload);

            if (!key) {
                return state;
            }

            const updatedFetchInfo = id ? {
                ...state.fetchInfo[key],
                [id]: {
                    fetched: true,
                    loading: false,
                    error: true
                }
            } : {
                fetched: true,
                loading: false,
                error: true
            }

            return Object.assign({}, state, {
                fetchInfo: {
                    ...state.fetchInfo,
                    [key]: updatedFetchInfo
                }
            });
        }

        case 'ALL_TASKS_FETCH_REQUEST_STARTED':
            return Object.assign({}, state, {
                allTasksLoading: true,
                allTasksFetched: false
            });

        case 'ALL_TASKS_FETCH_SUCCEEDED':
            return Object.assign({}, state, {
                allTasksLoading: false,
                allTasksFetched: true
            });

        case 'TASKS_ARCHIVE_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (action.payload.task_ids) {
                        if (action.payload.task_ids.includes(task.id)) {
                            return Object.assign({}, task, {
                                archived: action.payload.archived
                            });
                        }
                    } else if (action.payload.project_id && action.payload.completed) {
                        if (task.project === action.payload.project_id && task.completed) {
                            return Object.assign({}, task, {
                                archived: action.payload.archived
                            });
                        }
                    } else if (action.payload.task_group_id) {
                        if (task.task_group === action.payload.task_group_id) {
                            return Object.assign({}, task, {
                                archived: action.payload.archived
                            });
                        }
                    }

                    return task;
                })
            });

        case 'TASKS_COMPLETE_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (action.payload.taskIds.includes(task.id)) {
                        return Object.assign({}, task, {
                            completed: true,
                            completed_on: new Date()
                        });
                    }

                    return task;
                })
            });

        case 'TASK_ADD_SOCKET':
        case 'TASK_CREATE_REQUESTED':
            if (action.type === 'TASK_ADD_SOCKET') {
                const prevTask = state.items.find(task => isTempId(task.id) && action.payload.task.name === task.name && (action.payload.task.nice_id === task.nice_id || !task.nice_id));
                let nextItems = removeDupes([{...action.payload.task, created_at: prevTask?.created_at || action.payload.task.created_at }, ...state.items])
                    .filter(task => {
                        if (task === prevTask) {
                            return false;
                        }

                        return true;
                    });

                if (action.payload.task.task) {
                    nextItems = nextItems.map(task => {
                        if (task.id === action.payload.task.task) {
                            return {
                                ...task,
                                total_subtasks: incrementCount(task.total_subtasks)
                            }
                        }

                        return task;
                    });
                }

                return Object.assign({}, state, {
                    items: nextItems
                });
            }

            return Object.assign({}, state, {
                items: removeDupes([...state.items, action.payload.task])
            });

        case 'TASKS_CREATE_REQUESTED':
            return Object.assign({}, state, {
                items: removeDupes([...state.items, ...action.payload.createProps])
            });

        case 'TASK_CREATE_FAILED':
            return Object.assign({}, state, {
                items: state.items.filter(task => task !== action.payload.fragment)
            });

        case 'TASK_CREATE_SUCCEEDED':
            return Object.assign({}, state, {
                items: removeDupes(state.items
                    .map(task => {
                        if (task === action.payload.fragment) {
                            return {
                                ...task,
                                id: action.payload.taskId,
                                nice_id: action.payload.nice_id,
                                order: Number(action.payload.order),
                                project: action.payload.project,
                                assignees: action.payload.assignees,
                                milestone: action.payload.milestone,
                                due_date: action.payload.due_date,
                                hidden: action.payload.hidden
                            };
                        }

                        return task;
                    }))
            });

        case 'TASK_FETCH_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items.filter(task => task.id !== action.payload.taskId), {
                    ...(state.items.find(task => task.id === action.payload.taskId) || {}),
                    id: action.payload.taskId,
                    loading: true
                }]
            });

        case 'TASK_FETCH_FAILED':
            return Object.assign({}, state, {
                items: [...state.items.filter(task => task.id !== action.payload.taskId), {
                    id: action.payload.taskId,
                    error: true
                }]
            });

        case 'TASK_FETCH_SUCCEEDED':
            return Object.assign({}, state, {
                items: [...state.items.filter(task => task.id !== action.payload.task.id && task.nice_id !== action.payload.task.nice_id && task.id !== action.payload.task.nice_id), action.payload.task]
            });

        case 'TASK_UPDATE_REQUESTED':
        case 'TASK_UPDATE_SOCKET': {
            const { other, ...taskProps} = action.payload.taskProps || action.payload.task;
            const isSubtaskSocketUpdate = taskProps.task && action.type === 'TASK_UPDATE_SOCKET';
            const isSubtaskSocketConverted = other?.prev_task && action.type === 'TASK_UPDATE_SOCKET';

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        const nextProps = cleanObject(taskProps);

                        if (nextProps.task_group) {
                            nextProps.task = null;
                        }

                        return Object.assign({}, task, nextProps);
                    }

                    if (isSubtaskSocketUpdate && task.id === taskProps.task) {
                        if ('completed' in taskProps) {
                            if (taskProps.completed) {
                                return {
                                    ...task,
                                    completed_subtasks: incrementCount(task.completed_subtasks)
                                };
                            } else {
                                return {
                                    ...task,
                                    completed_subtasks: decrementCount(task.completed_subtasks)
                                };
                            }
                        }
                    }

                    if (isSubtaskSocketConverted) {
                         if (taskProps.task && task.id === taskProps.task) {
                            return {
                                ...task,
                                total_subtasks: incrementCount(task.total_subtasks)
                            };
                        }

                        if (other?.prev_task === task.id) {
                            return {
                                ...task,
                                total_subtasks: decrementCount(task.total_subtasks)
                            };
                        }
                    }

                    return task;
                })
            });
        }

        case 'TASKS_UPDATE_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const pair = action.payload.updatePairs.find(({ id }) => task.id === id);

                    if (pair) {
                        return Object.assign({}, task, cleanObject(pair.props));
                    }

                    return task;
                })
            });

        case 'TASKS_REORDER_REQUESTED':
        case 'TASKS_REORDER_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const taskOrderPair = action.payload.tasks.find(({ id }) => id === task.id);
                    if (action.payload.task && task.id === action.payload.task.id) {
                        return Object.assign({}, task, action.payload.task);
                    }

                    if (taskOrderPair && task.order !== taskOrderPair.order) {
                        return Object.assign({}, task, {
                            order: taskOrderPair.order
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_MOVE_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (action.payload.taskIds.includes(task.id)) {
                        return Object.assign({}, task, {
                            [action.payload.moveProps.type]: action.payload.moveProps.id
                        });
                    }

                    return task;
                })
            });

        case 'TASK_ARCHIVE_SOCKET':
        case 'TASK_UNARCHIVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            archived: action.type === 'TASK_ARCHIVE_SOCKET'
                        });
                    }

                    return task;
                })
            });

        case 'TASK_REMOVE_REQUESTED':
        case 'TASK_REMOVE_SOCKET': {
            let nextItems = state.items.filter(task => task.id !== action.payload.taskId);

            if (action.type === 'TASK_REMOVE_SOCKET' && action.payload.parentTaskId) {
                nextItems = nextItems.map(task => {
                    if (task.id === action.payload.parentTaskId) {
                        const nextTaskProps = {
                            total_subtasks: decrementCount(task.total_subtasks)
                        };

                        if (action.payload.completed) {
                            nextTaskProps.completed_subtasks = decrementCount(task.completed_subtasks);
                        }

                        return {
                            ...task,
                            ...nextTaskProps
                        };
                    }

                    return task;
                });
            }

            return Object.assign({}, state, {
                items: nextItems
            });
        }

        case 'TASKS_REMOVE_REQUESTED':
            if (action.payload.project_id && action.payload.archived) {
                return Object.assign({}, state, {
                    items: state.items.filter(task => !task.archived || task.project !== action.payload.project_id)
                });
            }

            if (action.payload.task_ids) {
                return Object.assign({}, state, {
                    items: state.items.filter(({ id }) => !action.payload.task_ids.includes(id))
                });
            }

            return state;

        case 'TASK_ASSIGN_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            assignees: [...new Set([...(task.assignees || []), ...action.payload.assignees])]
                        });
                    }

                    return task;
                })
            });

        case 'TASK_UNASSIGN_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            assignees: task.assignees.filter(userId => !action.payload.assignees.includes(userId))
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_ASSIGN_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const pair = action.payload.updatePairs.find(({ id }) => task.id === id);

                    if (pair) {
                        return Object.assign({}, task, {
                            assignees: [...new Set([...(task.assignees || []), ...pair.assignees])]
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_UNASSIGN_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const pair = action.payload.updatePairs.find(({ id }) => task.id === id);

                    if (pair) {
                        return Object.assign({}, task, {
                            assignees: task.assignees.filter(userId => !pair.assignees.includes(userId))
                        });
                    }

                    return task;
                })
            });


        case 'MILESTONE_UNTIE_REQUESTED':
        case 'MILESTONE_UNTIE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            milestone: null
                        });
                    }

                    return task;
                })
            });

        case 'TASK_GROUP_MOVE_ALL_TASKS_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.task_group === action.payload.sourceTaskGroupId) {
                        return Object.assign({}, task, {
                            task_group: action.payload.targetTaskGroupId,
                            project: action.payload.targetProjectId
                        });
                    }

                    return task;
                })
            });

        case 'MILESTONE_REMOVE_REQUESTED':
        case 'MILESTONE_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.milestone === action.payload.milestoneId) {
                        return Object.assign({}, task, {
                            milestone: null
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_FILTER_SET':
            return Object.assign({}, state, {
                filter: Object.assign({}, state.filter, action.payload.filter)
            });

        case 'MESSAGE_ADD_SOCKET':
        case 'MESSAGE_REMOVE_SOCKET':
            if (!action.payload.message || !action.payload.message.task || action.payload.message.subtype || !state.items.some(task => task.id === action.payload.message.task)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.message.task) {
                        return Object.assign({}, task, {
                            comments: action.type === 'MESSAGE_ADD_SOCKET' ? incrementCount(task.comments) : decrementCount(task.comments)
                        });
                    }

                    return task;
                })
            });

        case 'FILE_ADD_SOCKET':
            if (!action.payload.file?.task || !state.items.some(task => task.id === action.payload.file.task)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.file.task) {
                        return Object.assign({}, task, {
                            attachments: removeDupes([action.payload.file, ...(task.attachments || [])]),
                            // banner: task.banner || (isFileImage(action.payload.file.name) ? action.payload.file.url : undefined)
                        });
                    }

                    return task;
                })
            });

        case 'TASK_VISIBILITY_SET_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            hidden: !action.payload.visible ? [...(task.hidden || []), ...action.payload.roles] : (task.hidden || []).filter(r => !action.payload.roles.includes(r))
                        });
                    }

                    return task;
                })
            });

        case 'TASK_LABELS_ADD_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            labels: [...new Set([...(task.labels || []), ...action.payload.labels])]
                        });
                    }

                    return task;
                })
            });

        case 'TASK_LABELS_REMOVE_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            labels: (task.labels || []).filter(labelId => !action.payload.labels.includes(labelId))
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_LABELS_ADD_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const pair = action.payload.updatePairs.find(({ id }) => task.id === id);

                    if (pair) {
                        return Object.assign({}, task, {
                            labels: [...new Set([...(task.labels || []), ...pair.labels])]
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_LABELS_REMOVE_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const pair = action.payload.updatePairs.find(({ id }) => task.id === id);

                    if (pair) {
                        return Object.assign({}, task, {
                            labels: (task.labels || []).filter(userId => !pair.labels.includes(userId))
                        });
                    }

                    return task;
                })
            });

        case 'TASK_DOC_ADD_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            documents: [...new Set([action.payload.docId, ...(task.documents || [])])]
                        });
                    }

                    return task;
                })
            });

        case 'TASK_DOC_REMOVE_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            documents: (task.documents || []).filter(docId => docId !== action.payload.docId)
                        });
                    }

                    return task;
                })
            });

        case 'MILESTONE_TIE_TASKS_REQUESTED':
            if (!state.items.some(task => action.payload.tasks.includes(task.id))) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (action.payload.tasks.includes(task.id)) {
                        return Object.assign({}, task, {
                            milestone: action.payload.milestoneId
                        });
                    }

                    return task;
                })
            });

        case 'MILESTONE_UNTIE_TASKS_REQUESTED':
            if (!state.items.some(task => action.payload.tasks.includes(task.id))) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (action.payload.tasks.includes(task.id)) {
                        return Object.assign({}, task, {
                            milestone: null
                        });
                    }

                    return task;
                })
            });

        case 'MILESTONE_UPDATE_REQUESTED':
        case 'MILESTONE_UPDATE_SOCKET': {
            const milestoneProps = action.payload.milestoneProps || action.payload.milestone;
            if (!state.items.some(task => task.milestone === action.payload.milestoneId)) {
                return state;
            }

            if (milestoneProps.archived) {
                return Object.assign({}, state, {
                    items: [...state.items].map(task => {
                        if (task.milestone === action.payload.milestoneId) {
                            return Object.assign({}, task, {
                                milestone: null
                            });
                        }

                        return task;
                    })
                });
            }

            if (milestoneProps.start || milestoneProps.end) {
                return Object.assign({}, state, {
                    items: [...state.items].map(task => {
                        if (task.due_date && task.milestone === action.payload.milestoneId) {
                            if (new Date(task.due_date) < new Date(milestoneProps.start)) {
                                return Object.assign({}, task, {
                                    due_date: milestoneProps.start
                                });
                            }

                            if (new Date(task.due_date) > new Date(milestoneProps.end)) {
                                return Object.assign({}, task, {
                                    due_date: milestoneProps.end
                                });
                            }
                        }

                        return task;
                    })
                });
            }

            return state;
        }

        case 'CUSTOM_FIELD_VALUE_SET_REQUESTED':
            if (action.payload.association !== 'task' || !state.items.some(task => task.id === action.payload.associationId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.associationId) {
                        return Object.assign({}, task, {
                            fields: [...(task.fields || []).filter(field => field.id !== action.payload.fieldId), {
                                id: action.payload.fieldId,
                                value: action.payload.value
                            }]
                        });
                    }

                    return task;
                })
            });

        case 'CUSTOM_FIELDS_VALUE_SET_REQUESTED':
            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    const match = action.payload.updateProps.find(({ associationId }) => associationId === task.id);
                    if (match) {
                        return Object.assign({}, task, {
                            fields: [...(task.fields || []).filter(field => field.id !== match.fieldId), {
                                id: match.fieldId,
                                value: match.value
                            }]
                        });
                    }

                    return task;
                })
            });

        case 'CUSTOM_FIELDS_VALUES_FETCH_REQUESTED':
            if (action.payload.association !== 'task' || !state.items.some(task => task.id === action.payload.associationId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.associationId) {
                        return Object.assign({}, task, {
                            fields: task.fields || []
                        });
                    }

                    return task;
                })
            });

        case 'CUSTOM_FIELDS_VALUES_FETCH_SUCCEEDED':
            if (action.payload.association !== 'task' || !state.items.some(task => task.id === action.payload.associationId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.associationId) {
                        return Object.assign({}, task, {
                            fields: action.payload.values
                        });
                    }

                    return task;
                })
            });

        case 'USER_INVITE_CANCEL_SOCKET':
        case 'USER_REMOVE_REQUESTED':
        case 'PROJECT_USER_REMOVE_SOCKET':
        case 'TEAM_USER_REMOVE_SOCKET':
            return Object.assign({}, state, {
                items: [...state.items.map(task => {
                    if (action.payload.projectId && task.project !== action.payload.projectId) {
                        return task;
                    }

                    if (task.assignees && task.assignees.length && task.assignees.includes(action.payload.userId)) {
                        return Object.assign({}, task, {
                            assignees: task.assignees.filter(userId => userId !== action.payload.userId)
                        });
                    }

                    return task;
                })]
            });

        case 'RECURSION_RULE_ADD_SOCKET':
            if (!action.payload.rule.source_task || !state.items.some(task => task.id === action.payload.rule.source_task)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.rule.source_task) {
                        return Object.assign({}, task, {
                            rule: action.payload.rule.id
                        });
                    }

                    return task;
                })
            });

        case 'RECURSION_RULE_REMOVE_REQUESTED':
        case 'RECURSION_RULE_REMOVE_SOCKET':
            if (!state.items.some(task => task.rule === action.payload.ruleId)) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.rule === action.payload.ruleId) {
                        return Object.assign({}, task, {
                            rule: null
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_LINK_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId || action.payload.taskIds.includes(task.id))) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            linked_tasks: [...(task.linked_tasks || []), ...action.payload.taskIds]
                        });
                    }

                    if (action.payload.taskIds.includes(task.id)) {
                        return Object.assign({}, task, {
                            linked_tasks: [...(task.linked_tasks || []), action.payload.taskId]
                        });
                    }

                    return task;
                })
            });

        case 'TASKS_UNLINK_REQUESTED':
            if (!state.items.some(task => task.id === action.payload.taskId || action.payload.taskIds.includes(task.id))) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items].map(task => {
                    if (task.id === action.payload.taskId) {
                        return Object.assign({}, task, {
                            linked_tasks: (task.linked_tasks || []).filter(id => !action.payload.taskIds.includes(id))
                        });
                    }

                    if (action.payload.taskIds.includes(task.id)) {
                        return Object.assign({}, task, {
                            linked_tasks: (task.linked_tasks || []).filter(id => id !== action.payload.taskId)
                        });
                    }

                    return task;
                })
            });

        case 'PROJECT_REMOVE_REQUESTED':
        case 'PROJECT_REMOVE_SOCKET':
        case 'PROJECT_KICK_SOCKET':
        case 'PROJECT_UPDATE_SOCKET':
            if (action.type === 'PROJECT_UPDATE_SOCKET' && !('archived' in action.payload.project)) {
                return state;
            }

            if (!state.items.some(task => task.project === action.payload.projectId)) {
                return state;
            }

            if (action.payload.project && !action.payload.project.archived) {
                return state;
            }

            return Object.assign({}, state, {
                items: [...state.items.map(task => {
                    if (!task.project || task.project !== action.payload.projectId) {
                        return task;
                    }

                    return {
                        ...task,
                        archived: true
                    }
                })],
                fetchInfo: {
                    ...state.fetchInfo,
                    project: {
                        ...state.fetchInfo.project,
                        [action.payload.projectId]: {
                            fetched: false,
                            loading: false,
                            error: false
                        }
                    }

                }
            });

        default:
            return state;
    }
}

export default tasks;
